import React, { useState, useEffect } from 'react'
import { Link } from '@tanstack/react-router'
import {
  FileCheck,
  Search,
  Ban,
  Download,
  FileText,
} from 'lucide-react'
import { apiGetWithMeta } from '../lib/api'
import type { Invoice } from '@buddys-choice/shared'
import { DataTablePagination } from './DataTablePagination'
import { AppDataTable } from './AppDataTable'
import { SearchableSelect } from './SearchableSelect'

const INVOICE_STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'paid', label: 'Paid' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'void', label: 'Void' },
]

export const handleExportPdf = (inv: Invoice) => {
  const printWin = window.open('', '_blank', 'width=800,height=900')
  if (!printWin) return
  printWin.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Invoice ${inv.invoiceNumber} - Buddy's Choice</title>
        <style>
          body { font-family: 'Helvetica Neue', Arial, sans-serif; margin: 40px; color: #1e293b; background: #fff; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #e2e8f0; padding-bottom: 20px; }
          .logo { font-size: 24px; font-weight: bold; color: #8F6D3D; text-transform: uppercase; letter-spacing: 1px; }
          .sub { font-size: 12px; color: #64748b; margin-top: 4px; }
          .inv-title { text-align: right; }
          .inv-num { font-size: 20px; font-weight: bold; color: #0f172a; }
          .details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 30px 0; }
          .box { background: #f8fafc; padding: 15px; border-radius: 8px; font-size: 13px; border: 1px solid #e2e8f0; }
          .box-title { font-size: 11px; font-weight: bold; color: #64748b; text-transform: uppercase; margin-bottom: 8px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 13px; }
          th { background: #f1f5f9; text-align: left; padding: 10px; border-bottom: 2px solid #cbd5e1; font-size: 12px; }
          td { padding: 12px 10px; border-bottom: 1px solid #e2e8f0; }
          .right { text-align: right; }
          .totals { margin-top: 20px; width: 300px; margin-left: auto; font-size: 13px; }
          .tot-row { display: flex; justify-content: space-between; padding: 6px 0; }
          .tot-row.grand { font-size: 16px; font-weight: bold; color: #8F6D3D; border-top: 2px solid #e2e8f0; padding-top: 10px; }
          .footer { margin-top: 50px; text-align: center; font-size: 11px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 15px; }
          @media print {
            body { margin: 20px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="logo">Buddy's Choice</div>
            <div class="sub">Luxury Gold Plated Fashion Jewellery</div>
            <div class="sub">GSTIN: 33AAACB1234C1Z5 · HSN 7113</div>
          </div>
          <div class="inv-title">
            <div class="inv-num">${inv.invoiceNumber}</div>
            <div class="sub">Date: ${new Date(inv.issuedAt).toLocaleDateString('en-IN')}</div>
            <div class="sub">Status: <strong>${inv.status.toUpperCase()}</strong></div>
          </div>
        </div>

        <div class="details-grid">
          <div class="box">
            <div class="box-title">Billed To</div>
            <strong>${inv.customerName}</strong><br/>
            Phone: ${inv.customerPhone}<br/>
            ${inv.customerEmail ? 'Email: ' + inv.customerEmail : ''}
          </div>
          <div class="box">
            <div class="box-title">Invoice Summary</div>
            Invoice ID: ${inv.id}<br/>
            Order ID: ${inv.orderId}<br/>
            Payment Status: ${inv.status}
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th class="right">Subtotal</th>
              <th class="right">CGST (3%)</th>
              <th class="right">SGST (3%)</th>
              <th class="right">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Gold Plated Jewellery Order (${inv.invoiceNumber})</td>
              <td class="right">₹${inv.subtotal.toFixed(2)}</td>
              <td class="right">₹${inv.taxCgst.toFixed(2)}</td>
              <td class="right">₹${inv.taxSgst.toFixed(2)}</td>
              <td class="right"><strong>₹${inv.total.toFixed(2)}</strong></td>
            </tr>
          </tbody>
        </table>

        <div class="totals">
          <div class="tot-row"><span>Subtotal:</span> <span>₹${inv.subtotal.toFixed(2)}</span></div>
          <div class="tot-row"><span>CGST (3%):</span> <span>₹${inv.taxCgst.toFixed(2)}</span></div>
          <div class="tot-row"><span>SGST (3%):</span> <span>₹${inv.taxSgst.toFixed(2)}</span></div>
          <div class="tot-row grand"><span>Total Amount:</span> <span>₹${inv.total.toFixed(2)}</span></div>
        </div>

        <div class="footer">
          Thank you for choosing Buddy's Choice! Handcrafted Luxury Jewellery.<br/>
          This is a computer-generated tax invoice.
        </div>

        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
    </html>
  `)
  printWin.document.close()
}

export const AdminInvoices: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const pageSize = 10

  useEffect(() => {
    fetchInvoices()
  }, [search, statusFilter, page])

  const fetchInvoices = () => {
    setLoading(true)
    apiGetWithMeta<Invoice[]>('invoices', {
      search: search || undefined,
      status: statusFilter || undefined,
      page,
      pageSize,
    })
      .then((res) => {
        setInvoices(res.data || [])
        setTotal(res.meta?.total ?? 0)
        setTotalPages(res.meta?.totalPages ?? 1)
      })
      .catch((err) => console.error('Fetch invoices error:', err))
      .finally(() => setLoading(false))
  }

  const formatCurrency = (amt: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amt)

  const invoiceColumns = [
    {
      name: 'Invoice No',
      sortable: true,
      sortValue: (inv: Invoice) => inv.invoiceNumber,
      selector: (inv: Invoice) => (
        <span className="font-mono font-bold text-[var(--gold-dark)]">{inv.invoiceNumber}</span>
      ),
    },
    {
      name: 'Customer',
      cell: (inv: Invoice) => (
        <div>
          {inv.customerName}
          <span className="block text-[10px] font-mono text-[var(--text-primary)]/50">{inv.customerPhone}</span>
        </div>
      ),
    },
    {
      name: 'Subtotal',
      selector: (inv: Invoice) => formatCurrency(inv.subtotal),
    },
    {
      name: 'CGST (3%)',
      selector: (inv: Invoice) => formatCurrency(inv.taxCgst),
    },
    {
      name: 'SGST (3%)',
      selector: (inv: Invoice) => formatCurrency(inv.taxSgst),
    },
    {
      name: 'Total',
      sortable: true,
      sortValue: (inv: Invoice) => inv.total,
      selector: (inv: Invoice) => (
        <span className="font-serif font-bold text-[var(--gold-dark)]">{formatCurrency(inv.total)}</span>
      ),
    },
    {
      name: 'Status',
      cell: (inv: Invoice) => <span className={`badge badge-${inv.status}`}>{inv.status}</span>,
    },
    {
      name: 'Actions',
      cell: (inv: Invoice) => (
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => handleExportPdf(inv)}
            className="p-1.5 text-[var(--gold-main)] hover:bg-[var(--gold-main)]/10 rounded flex items-center gap-1 text-[11px] font-semibold border border-[var(--gold-main)]/30"
            title="Export PDF Invoice"
          >
            <Download className="w-3.5 h-3.5" /> PDF
          </button>
          {inv.status === 'unpaid' && (
            <Link
              to="/admin/invoices/$invoiceId/mark-paid"
              params={{ invoiceId: inv.id }}
              className="btn btn-secondary btn-sm text-[11px] px-2 py-1 admin-action-chip--success border"
            >
              Mark Paid
            </Link>
          )}
          {inv.status !== 'void' && (
            <Link
              to="/admin/invoices/$invoiceId/void"
              params={{ invoiceId: inv.id }}
              className="p-1.5 text-red-400 hover:bg-red-500/10 rounded"
              title="Soft void invoice"
            >
              <Ban className="w-4 h-4" />
            </Link>
          )}
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-serif font-bold text-[var(--text-primary)]">GST Invoices</h2>
          <p className="text-xs text-[var(--text-primary)]/50 mt-1">
            Itemized 3% CGST + 3% SGST invoices (HSN 7113). Zero hard-delete — soft void only.
          </p>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="glass-card p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            placeholder="Search by Invoice #, Customer Name, or Phone..."
            className="form-input text-xs pl-9"
          />
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-[var(--gold-dark)]" />
        </div>

        <SearchableSelect
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1) }}
          options={INVOICE_STATUS_OPTIONS}
          placeholder="All Statuses"
          aria-label="Filter by invoice status"
          className="sm:w-48"
        />
      </div>

      {/* Invoices — desktop table */}
      <div className="glass-card p-4 hidden md:block">
        <AppDataTable
          columns={invoiceColumns}
          data={invoices}
          keyField={(inv) => inv.id}
          progressPending={loading}
          noDataComponent="No GST invoices found."
        />
      </div>

      {/* Invoices — mobile cards */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="admin-mobile-card text-center py-8">
            <span className="spinner text-[var(--gold-dark)]" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="admin-mobile-card text-center py-8 text-xs text-[var(--text-primary)]/40">
            No GST invoices found.
          </div>
        ) : (
          invoices.map((inv) => (
            <div key={inv.id} className="admin-mobile-card">
              <div className="flex justify-between items-start gap-2">
                <div>
                  <p className="font-mono text-sm font-bold text-[var(--gold-dark)]">{inv.invoiceNumber}</p>
                  <p className="text-sm font-medium text-[var(--text-primary)] mt-0.5">{inv.customerName}</p>
                  <p className="font-mono text-[10px] text-[var(--text-muted)]">{inv.customerPhone}</p>
                </div>
                <span className={`badge badge-${inv.status}`}>{inv.status}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-[10px]">
                <div>
                  <p className="admin-mobile-card-label">Subtotal</p>
                  <p className="font-mono">{formatCurrency(inv.subtotal)}</p>
                </div>
                <div>
                  <p className="admin-mobile-card-label">CGST</p>
                  <p className="font-mono">{formatCurrency(inv.taxCgst)}</p>
                </div>
                <div>
                  <p className="admin-mobile-card-label">SGST</p>
                  <p className="font-mono">{formatCurrency(inv.taxSgst)}</p>
                </div>
              </div>
              <div className="flex justify-between items-center mt-2 pt-2 border-t border-[var(--border)]">
                <span className="admin-mobile-card-label">Total</span>
                <span className="font-serif font-bold text-[var(--gold-dark)]">{formatCurrency(inv.total)}</span>
              </div>
              <div className="admin-mobile-card-actions">
                {inv.status === 'unpaid' && (
                  <Link
                    to="/admin/invoices/$invoiceId/mark-paid"
                    params={{ invoiceId: inv.id }}
                    className="admin-action-chip admin-action-chip--info"
                  >
                    Mark Paid
                  </Link>
                )}
                {inv.status !== 'void' && (
                  <Link
                    to="/admin/invoices/$invoiceId/void"
                    params={{ invoiceId: inv.id }}
                    className="admin-action-chip admin-action-chip--danger"
                  >
                    <Ban className="w-4 h-4" /> Void
                  </Link>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <DataTablePagination
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={pageSize}
        onPageChange={setPage}
        className="pt-2"
      />
    </div>
  )
}
